import React, { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { format } from 'date-fns';
import type { AttendanceJustification, Employee } from '../lib/supabase';

function getWorkingDaysInRange(startStr: string, endStr: string): string[] {
  const result: string[] = [];
  const start = new Date(startStr + 'T12:00:00');
  const end   = new Date(endStr   + 'T12:00:00');
  const cur   = new Date(start);
  while (cur <= end) {
    const d = cur.getDay();
    if (d !== 0 && d !== 6) result.push(format(cur, 'yyyy-MM-dd'));
    cur.setDate(cur.getDate() + 1);
  }
  return result;
}

const TYPE_LABEL: Record<string, string> = {
  absence: 'Falta', tardiness: 'Tardanza', vacation_request: 'Vacaciones',
};
const STATUS_BADGE: Record<string, string> = {
  pending: 'badge-warning', approved: 'badge-neutral', rejected: 'badge-error',
};
const STATUS_LABEL: Record<string, string> = {
  pending: 'Pendiente', approved: 'Aprobada', rejected: 'Rechazada',
};

interface JustifWithEmployee extends AttendanceJustification {
  employee?: Employee;
}

export function JustificationsPage() {
  const { company, profile } = useAuth();
  const [items, setItems]         = useState<JustifWithEmployee[]>([]);
  const [loading, setLoading]     = useState(true);
  const [filter, setFilter]       = useState<'pending' | 'approved' | 'rejected' | 'all'>('pending');
  const [reviewing, setReviewing] = useState<JustifWithEmployee | null>(null);
  const [reviewNote, setReviewNote] = useState('');
  const [saving, setSaving]       = useState(false);
  const [toast, setToast]         = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => { if (company) load(); }, [company, filter]);

  function showToast(msg: string, type: 'success' | 'error' = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }

  async function load() {
    setLoading(true);
    let q = supabase
      .from('attendance_justifications')
      .select('*, employee:employees(first_name, last_name, position, department)')
      .eq('company_id', company!.id)
      .order('created_at', { ascending: false });
    if (filter !== 'all') q = q.eq('status', filter);
    const { data } = await q;
    setItems(data || []);
    setLoading(false);
  }

  async function handleReview(approved: boolean) {
    if (!reviewing) return;
    setSaving(true);

    // 1. Actualizar el estado de la justificación
    await supabase.from('attendance_justifications').update({
      status:      approved ? 'approved' : 'rejected',
      reviewed_by: profile!.id,
      reviewed_at: new Date().toISOString(),
      review_note: reviewNote.trim() || null,
    }).eq('id', reviewing.id);

    // 2. Faltas y tardanzas aprobadas — eliminar el descuento
    if (approved && reviewing.attendance_id && reviewing.type !== 'vacation_request') {
      if (reviewing.type === 'absence') {
        await supabase.from('attendance').update({
          status: 'permission', late_minutes: 0,
        }).eq('id', reviewing.attendance_id);
      } else if (reviewing.type === 'tardiness') {
        await supabase.from('attendance').update({ late_minutes: 0 })
          .eq('id', reviewing.attendance_id);
      }
    }

    // 3. Solicitudes de vacaciones
    if (reviewing.type === 'vacation_request' && reviewing.employee_id) {
      const startDate  = reviewing.vacation_start_date ?? reviewing.date;
      const endDate    = reviewing.vacation_end_date   ?? reviewing.date;
      const daysCount  = reviewing.vacation_days_count ?? 1;
      const year       = new Date(startDate).getFullYear();

      if (approved) {
        // ── 3a. Crear / actualizar registros de asistencia como "vacation" ──
        const workingDays = getWorkingDaysInRange(startDate, endDate);

        if (workingDays.length > 0) {
          // Buscar registros existentes en el rango
          const { data: existing } = await supabase
            .from('attendance')
            .select('id, date')
            .eq('employee_id', reviewing.employee_id)
            .gte('date', startDate)
            .lte('date', endDate);

          const existingDateSet = new Set((existing || []).map(r => r.date));
          const existingIds     = (existing || []).map(r => r.id);

          // Actualizar los que ya existen a "vacation"
          if (existingIds.length > 0) {
            await supabase.from('attendance').update({
              status: 'vacation', late_minutes: 0, overtime_minutes: 0,
            }).in('id', existingIds);
          }

          // Insertar los que aún no existen
          const toInsert = workingDays
            .filter(d => !existingDateSet.has(d))
            .map(d => ({
              company_id:       reviewing.company_id,
              employee_id:      reviewing.employee_id,
              date:             d,
              status:           'vacation' as const,
              late_minutes:     0,
              overtime_minutes: 0,
              hours_worked:     0,
            }));

          if (toInsert.length > 0) {
            await supabase.from('attendance').insert(toInsert);
          }
        }

        // ── 3b. Actualizar saldo: días_tomados += count, días_pendientes -= count ──
        const { data: bal } = await supabase
          .from('vacation_balances').select('*')
          .eq('employee_id', reviewing.employee_id).eq('year', year).single();

        if (bal) {
          await supabase.from('vacation_balances').update({
            days_taken:   bal.days_taken + daysCount,
            days_pending: Math.max(0, bal.days_pending - daysCount),
          }).eq('id', bal.id);
        }
      } else {
        // ── 3c. Al rechazar: liberar los días pendientes ──────────────────
        const { data: bal } = await supabase
          .from('vacation_balances').select('*')
          .eq('employee_id', reviewing.employee_id).eq('year', year).single();

        if (bal) {
          await supabase.from('vacation_balances').update({
            days_pending: Math.max(0, bal.days_pending - daysCount),
          }).eq('id', bal.id);
        }
      }
    }

    setSaving(false);
    setReviewing(null);
    setReviewNote('');
    showToast(approved ? 'Justificación aprobada' : 'Justificación rechazada');
    load();
  }

  const pendingCount = items.filter(i => i.status === 'pending').length;

  return (
    <div className="p-8 max-w-5xl mx-auto space-y-8">
      {toast && (
        <div className={`fixed top-6 right-6 z-50 animate-fade-in flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-white text-sm font-bold ${toast.type === 'success' ? 'bg-green-600' : 'bg-error'}`}>
          <span className="material-symbols-outlined text-[18px]">{toast.type === 'success' ? 'check_circle' : 'error'}</span>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-4xl font-extrabold tracking-tight text-on-surface font-headline">Justificaciones</h1>
          <p className="text-on-surface-variant mt-1">Revisión de faltas, tardanzas y solicitudes de vacaciones</p>
        </div>
        {pendingCount > 0 && filter !== 'pending' && (
          <span className="bg-error text-on-error text-xs font-bold px-3 py-1.5 rounded-full">
            {pendingCount} pendiente{pendingCount > 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Filtros */}
      <div className="flex gap-2">
        {(['pending', 'all', 'approved', 'rejected'] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${
              filter === f ? 'btn-primary text-white' : 'bg-surface-container-low text-on-surface-variant hover:bg-surface-container-high'
            }`}
          >
            {f === 'pending' ? `Pendientes${pendingCount > 0 ? ` (${pendingCount})` : ''}` :
             f === 'all' ? 'Todas' : f === 'approved' ? 'Aprobadas' : 'Rechazadas'}
          </button>
        ))}
      </div>

      {/* Lista */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="py-12 flex items-center justify-center">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center">
            <span className="material-symbols-outlined text-4xl text-outline-variant">task_alt</span>
            <p className="text-on-surface-variant text-sm mt-2">
              {filter === 'pending' ? 'No hay justificaciones pendientes' : 'No hay justificaciones'}
            </p>
          </div>
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr className="bg-surface-container-low">
                {['Empleado', 'Fecha', 'Tipo', 'Motivo', 'Estado', 'Acciones'].map(h => (
                  <th key={h} className="px-5 py-3 text-[10px] font-bold text-on-surface-variant uppercase tracking-widest">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/10">
              {items.map(item => (
                <tr key={item.id} className="hover:bg-surface-container-low/30 transition-colors">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xs flex-shrink-0">
                        {item.employee?.first_name?.[0]}{item.employee?.last_name?.[0]}
                      </div>
                      <div>
                        <p className="text-sm font-bold text-on-surface">
                          {item.employee?.first_name} {item.employee?.last_name}
                        </p>
                        <p className="text-xs text-on-surface-variant">{item.employee?.position}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-sm font-mono text-on-surface">
                    {item.vacation_start_date
                      ? <>{item.vacation_start_date}{item.vacation_end_date && item.vacation_end_date !== item.vacation_start_date ? <><br/><span className="text-on-surface-variant text-xs">al {item.vacation_end_date}</span></> : ''}</>
                      : item.date}
                  </td>
                  <td className="px-5 py-4">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                      item.type === 'absence' ? 'bg-red-50 text-red-700' :
                      item.type === 'tardiness' ? 'bg-amber-50 text-amber-700' :
                      'bg-blue-50 text-blue-700'
                    }`}>
                      {TYPE_LABEL[item.type]}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-sm text-on-surface-variant max-w-xs">
                    <p className="truncate">{item.reason}</p>
                    {item.review_note && (
                      <p className="text-xs text-primary italic mt-0.5">Nota: {item.review_note}</p>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <span className={STATUS_BADGE[item.status]}>{STATUS_LABEL[item.status]}</span>
                  </td>
                  <td className="px-5 py-4">
                    {item.status === 'pending' ? (
                      <button
                        onClick={() => { setReviewing(item); setReviewNote(''); }}
                        className="text-xs font-bold text-primary hover:underline flex items-center gap-1"
                      >
                        <span className="material-symbols-outlined text-[16px]">rate_review</span>
                        Revisar
                      </button>
                    ) : (
                      <span className="text-xs text-on-surface-variant">
                        {item.reviewed_at ? new Date(item.reviewed_at).toLocaleDateString('es-PE') : '—'}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal de revisión */}
      {reviewing && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-surface-container-lowest rounded-2xl shadow-2xl w-full max-w-md animate-fade-in">
            <div className="flex items-center justify-between p-5 border-b border-outline-variant/10">
              <h2 className="text-lg font-bold font-headline">Revisar justificación</h2>
              <button onClick={() => setReviewing(null)} className="p-2 hover:bg-surface-container-high rounded-lg">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="p-5 space-y-4">
              {/* Info */}
              <div className="p-4 bg-surface-container-low rounded-xl space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-on-surface-variant">Empleado</span>
                  <span className="font-bold">{reviewing.employee?.first_name} {reviewing.employee?.last_name}</span>
                </div>
                {reviewing.type === 'vacation_request' && reviewing.vacation_start_date ? (
                  <>
                    <div className="flex justify-between">
                      <span className="text-on-surface-variant">Período solicitado</span>
                      <span className="font-bold">
                        {reviewing.vacation_start_date}
                        {reviewing.vacation_end_date && reviewing.vacation_end_date !== reviewing.vacation_start_date
                          ? ` al ${reviewing.vacation_end_date}` : ''}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-on-surface-variant">Días hábiles</span>
                      <span className="font-bold text-blue-700">{reviewing.vacation_days_count ?? 1}</span>
                    </div>
                  </>
                ) : (
                  <div className="flex justify-between">
                    <span className="text-on-surface-variant">Fecha</span>
                    <span className="font-bold">{reviewing.date}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-on-surface-variant">Tipo</span>
                  <span className="font-bold">{TYPE_LABEL[reviewing.type]}</span>
                </div>
              </div>

              {/* Motivo */}
              <div className="p-4 bg-primary/5 rounded-xl border border-primary/15">
                <p className="text-xs font-bold text-on-surface-variant uppercase tracking-wider mb-1">Motivo del empleado</p>
                <p className="text-sm text-on-surface">{reviewing.reason}</p>
              </div>

              {/* Efecto si aprueba */}
              <div className="p-3 bg-green-50 border border-green-200 rounded-xl text-xs text-green-800">
                <strong>Si apruebas:</strong>{' '}
                {reviewing.type === 'absence' && 'La falta se cambiará a "Permiso" y NO se descontará del salario.'}
                {reviewing.type === 'tardiness' && 'Los minutos de tardanza se pondrán en 0 y NO se descontarán.'}
                {reviewing.type === 'vacation_request' && (
                  `Se crearán ${reviewing.vacation_days_count ?? 1} registro${(reviewing.vacation_days_count ?? 1) !== 1 ? 's' : ''} de asistencia como "Vacaciones" y se actualizará el saldo del empleado.`
                )}
              </div>

              {/* Nota del admin (opcional) */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">
                  Nota para el empleado (opcional)
                </label>
                <input
                  className="input-base"
                  placeholder="Ej: Aprobado, presenta certificado médico..."
                  value={reviewNote}
                  onChange={e => setReviewNote(e.target.value)}
                />
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t border-outline-variant/10">
              <button
                onClick={() => handleReview(false)}
                disabled={saving}
                className="flex-1 bg-error text-white py-2.5 rounded-lg font-bold text-sm hover:opacity-90 transition-all disabled:opacity-60 flex items-center justify-center gap-2"
              >
                <span className="material-symbols-outlined text-[16px]">close</span>
                Rechazar
              </button>
              <button
                onClick={() => handleReview(true)}
                disabled={saving}
                className="flex-1 bg-green-600 text-white py-2.5 rounded-lg font-bold text-sm hover:opacity-90 transition-all disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {saving ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <span className="material-symbols-outlined text-[16px]">check</span>}
                Aprobar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
